'use client';

import React, { useMemo, useState } from 'react';
import { useDebounce } from 'use-debounce';
import { CircularProgress, Checkbox } from 'src/components';
import { useGetAllTerritoriesQuery } from 'src/api/redux';
import { useAnalytics } from 'src/hooks/analytics';
import { useAuth } from 'src/context/auth';
import { FontAwesomeIcon as Icon } from '@fortawesome/react-fontawesome';
import {
  faChevronDown,
  faChevronRight,
} from '@fortawesome/pro-solid-svg-icons';

const AnalyticsTerritoryPanel: React.FC = () => {
  const { user } = useAuth();
  const {
    geographySearch,
    tempSelectedGeographies,
    toggleTempGeography,
    toggleAllTempGeographies,
  } = useAnalytics() || {};

  // Debounce the search query before sending to API
  const [debouncedSearchQuery] = useDebounce(geographySearch, 300);

  // Track expanded rep folders
  const [expandedReps, setExpandedReps] = useState<string[]>([]);

  // Primary-only filter
  const [showPrimaryOnly, setShowPrimaryOnly] = useState(false);

  // Fetch all territories using RTK Query with search parameter
  const { data: allTerritories, isFetching } = useGetAllTerritoriesQuery({
    search: debouncedSearchQuery || undefined,
    ...(showPrimaryOnly && { is_canonical: true }),
  });

  // Group territories by current user vs other reps (NO local filtering)
  const { myTerritories, repTerritoryGroups } = useMemo(() => {
    if (!allTerritories?.length || !user)
      return {
        myTerritories: [],
        repTerritoryGroups: {},
      };

    // Filter and sort user's own territories
    const myTerritories = allTerritories
      .filter((territory) => territory.owner?.id === user.id)
      .sort((a, b) => {
        // Sort canonical first, then alphabetically
        if (a.is_canonical !== b.is_canonical) return a.is_canonical ? -1 : 1;
        return a.name.localeCompare(b.name);
      });

    // Group other territories by rep name
    const repGroups = allTerritories
      .filter((territory) => territory.owner?.id !== user.id)
      .reduce((acc, territory) => {
        const repName = `${territory.owner.first_name} ${territory.owner.last_name}`;
        if (!acc[repName]) acc[repName] = [];
        acc[repName].push(territory);
        return acc;
      }, {} as Record<string, MedScout.Territory[]>);

    // Sort territories within each rep group
    Object.keys(repGroups).forEach((repName) => {
      repGroups[repName].sort((a, b) => {
        // Sort canonical first, then alphabetically
        if (a.is_canonical !== b.is_canonical) return a.is_canonical ? -1 : 1;
        return a.name.localeCompare(b.name);
      });
    });

    return {
      myTerritories,
      repTerritoryGroups: repGroups,
    };
  }, [allTerritories, user]);

  const handleToggleRep = (repName: string) => {
    setExpandedReps((prev) =>
      prev.includes(repName)
        ? prev.filter((name) => name !== repName)
        : [...prev, repName]
    );
  };

  // Handle select all for user's territories
  const handleSelectAllMyTerritories = () => {
    const territoryObjects = myTerritories.map((territory) => ({
      id: territory.id,
      name: territory.name,
      type: 'territory' as const,
    }));
    toggleAllTempGeographies?.(territoryObjects, 'territory');
  };

  // Handle select all for a specific rep's territories
  const handleSelectAllRepTerritories = (territories: MedScout.Territory[]) => {
    const territoryObjects = territories.map((territory) => ({
      id: territory.id,
      name: territory.name,
      type: 'territory' as const,
    }));
    toggleAllTempGeographies?.(territoryObjects, 'territory');
  };

  // Check if all of user's territories are selected
  const allMyTerritoriesSelected =
    myTerritories.length > 0 &&
    myTerritories.every((territory) =>
      tempSelectedGeographies?.includes(territory.id)
    );

  // Check if all territories for a rep are selected
  const areAllRepTerritoriesSelected = (territories: MedScout.Territory[]) => {
    return (
      territories.length > 0 &&
      territories.every((territory) =>
        tempSelectedGeographies?.includes(territory.id)
      )
    );
  };

  // All visible territories across all groups (for cross-group Select All)
  const allVisibleTerritories = useMemo(() => {
    const repTerritories = Object.values(repTerritoryGroups).flat();
    return [...myTerritories, ...repTerritories];
  }, [myTerritories, repTerritoryGroups]);

  // Handle cross-group select all (primary filter only)
  const handleSelectAllVisible = () => {
    const territoryObjects = allVisibleTerritories.map((territory) => ({
      id: territory.id,
      name: territory.name,
      type: 'territory' as const,
    }));
    toggleAllTempGeographies?.(territoryObjects, 'territory');
  };

  const allVisibleSelected =
    allVisibleTerritories.length > 0 &&
    allVisibleTerritories.every((territory) =>
      tempSelectedGeographies?.includes(territory.id)
    );

  if (isFetching && !allTerritories) {
    return (
      <div className="w-full flex items-center justify-center px-4 py-8">
        <CircularProgress size="sm" color="neutral" />
      </div>
    );
  }

  return (
    <div className="flex-1 overflow-y-auto">
      {/* Primary filter + Select All */}
      <div className="px-6 py-3 border-b border-neutral-100 flex flex-col gap-3">
        <div
          onClick={() => setShowPrimaryOnly((prev) => !prev)}
          className="inline-flex justify-start items-center gap-2 cursor-pointer"
          data-testid="primary-filter-checkbox"
        >
          <Checkbox
            size="sm"
            checked={showPrimaryOnly}
            onChange={() => setShowPrimaryOnly((prev) => !prev)}
            aria-label="Show primary territories only"
          />
          <div className="text-secondary text-sm font-normal font-['Inter'] leading-5">
            Show Primary Territories Only
          </div>
        </div>

        {allVisibleTerritories.length > 0 && (
          <div
            onClick={handleSelectAllVisible}
            className="inline-flex justify-start items-center gap-2 cursor-pointer"
            data-testid="select-all-territories"
          >
            <Checkbox
              size="sm"
              checked={allVisibleSelected || false}
              onChange={handleSelectAllVisible}
              aria-label="Select all territories"
            />
            <div className="text-secondary text-sm font-normal font-['Inter'] leading-5">
              Select All
            </div>
          </div>
        )}
      </div>

      {/* Current User's Territories */}
      {myTerritories.length > 0 && (
        <>
          <div className="flex items-center gap-2 px-4 py-2 text-xs font-medium text-neutral-700 border-b border-neutral-100">
            {user?.first_name} {user?.last_name}
            <span className="text-xs text-neutral-500 ml-auto">
              ({myTerritories.length})
            </span>
          </div>

          <div className="w-full px-6 inline-flex flex-col justify-start items-start gap-4 py-4">
            {/* Select All for user's territories (hidden when primary filter active) */}
            {!showPrimaryOnly && (
              <>
                <div
                  onClick={handleSelectAllMyTerritories}
                  className="inline-flex justify-start items-center gap-2 cursor-pointer"
                >
                  <Checkbox
                    size="sm"
                    checked={allMyTerritoriesSelected || false}
                    onChange={handleSelectAllMyTerritories}
                    aria-label="Select all my territories"
                  />
                  <div className="text-secondary text-sm font-normal font-['Inter'] leading-5">
                    Select All
                  </div>
                </div>

                <div className="self-stretch h-0 border-b border-neutral-300" />
              </>
            )}

            {myTerritories.map((territory) => (
              <div
                key={territory.id}
                onClick={() =>
                  toggleTempGeography?.(
                    territory.id,
                    territory.name,
                    'territory'
                  )
                }
                className="inline-flex justify-start items-center gap-2 cursor-pointer"
              >
                <Checkbox
                  size="sm"
                  checked={
                    tempSelectedGeographies?.includes(territory.id) || false
                  }
                  onChange={() =>
                    toggleTempGeography?.(
                      territory.id,
                      territory.name,
                      'territory'
                    )
                  }
                  aria-label={`Select ${territory.name}`}
                />
                <div className="flex items-center gap-1">
                  <span
                    className={
                      territory.is_canonical
                        ? 'text-sky-600 font-medium text-sm font-["Inter"] leading-5'
                        : 'text-secondary text-sm font-normal font-["Inter"] leading-5'
                    }
                  >
                    {territory.name}
                  </span>
                  {territory.is_canonical && (
                    <span className="text-xs text-neutral-500">
                      (primary territory)
                    </span>
                  )}
                </div>
              </div>
            ))}
          </div>
        </>
      )}

      {/* Rep Territory Groups (Accordions) */}
      {Object.entries(repTerritoryGroups)
        .sort(([a], [b]) => a.localeCompare(b))
        .map(([repName, territories]) => {
          const isExpanded = expandedReps.includes(repName);
          const allRepTerritoriesSelected =
            areAllRepTerritoriesSelected(territories);

          return (
            <div key={repName} className="border-t border-neutral-100">
              {/* Rep Header */}
              <div
                onClick={() => handleToggleRep(repName)}
                className="flex items-center gap-2 px-4 py-2 hover:bg-neutral-100 cursor-pointer text-xs font-medium text-neutral-700"
              >
                <Icon
                  icon={isExpanded ? faChevronDown : faChevronRight}
                  className="w-3 h-3 text-neutral-500"
                />
                {repName}
                <span className="text-xs text-neutral-500 ml-auto">
                  ({territories.length})
                </span>
              </div>

              {/* Territories list */}
              {isExpanded && (
                <div
                  className="w-full pl-6 pr-6 inline-flex flex-col
                justify-start items-start gap-4 py-4"
                >
                  {/* Select All for this rep's territories (hidden when primary filter active) */}
                  {!showPrimaryOnly && (
                    <>
                      <div
                        onClick={() =>
                          handleSelectAllRepTerritories(territories)
                        }
                        className="inline-flex justify-start items-center gap-2 cursor-pointer"
                      >
                        <Checkbox
                          size="sm"
                          checked={allRepTerritoriesSelected || false}
                          onChange={() =>
                            handleSelectAllRepTerritories(territories)
                          }
                          aria-label={`Select all ${repName} territories`}
                        />
                        <div className="text-secondary text-sm font-normal font-['Inter'] leading-5">
                          Select All
                        </div>
                      </div>

                      <div className="self-stretch h-0 border-b border-neutral-300" />
                    </>
                  )}

                  {/* Individual territories */}
                  {territories.map((territory) => (
                    <div
                      key={territory.id}
                      onClick={() =>
                        toggleTempGeography?.(
                          territory.id,
                          territory.name,
                          'territory'
                        )
                      }
                      className="inline-flex justify-start items-center gap-2 cursor-pointer"
                    >
                      <Checkbox
                        size="sm"
                        checked={
                          tempSelectedGeographies?.includes(territory.id) ||
                          false
                        }
                        onChange={() =>
                          toggleTempGeography?.(
                            territory.id,
                            territory.name,
                            'territory'
                          )
                        }
                        aria-label={`Select ${territory.name}`}
                      />
                      <span
                        className={`text-sm font-["Inter"] leading-5 ${
                          territory.is_canonical
                            ? 'text-sky-600 '
                            : 'text-secondary '
                        }`}
                      >
                        {territory.name}
                      </span>
                    </div>
                  ))}
                </div>
              )}
            </div>
          );
        })}

      {isFetching && (
        <div className="w-full flex items-center justify-center px-4 py-2 text-center text-xs text-neutral-500">
          <CircularProgress size="sm" color="neutral" />
        </div>
      )}
    </div>
  );
};

export default AnalyticsTerritoryPanel;
